import React from 'react'
import { useIntl } from 'react-intl'

import ConfirmationDetails from './Common/ConfirmationDetails'
import ExtraDetails from './Common/ExtraDetails'
import StartAndEndPlaces from './Common/StartAndEndPlaces'
import SummaryImage from './Common/SummaryImage'
import DefaultSummaryModalFooter from './Common/SummaryModalFooter/DefaultSummaryModalFooter'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import GooglePlaceRating from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRating'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import { ItemMetadata } from 'tripPlanner/models'
import type { ThingToDoItem } from 'tripPlanner/types/tripItem'
import { maybePlaceId } from 'tripPlanner/utils'
import { getDisplayableCustomThingToDoName } from 'tripPlanner/utils/itemConversions/common'

interface Props {
  item: ThingToDoItem
}

function ThingTodoItemSummary(props: Props) {
  const { item } = props
  const intl = useIntl()

  const isSameEndPlace =
    'id' in item.startPlace &&
    'id' in item.endPlace &&
    item.startPlace.id &&
    item.startPlace.id === item.endPlace.id

  const placeId = maybePlaceId(item.startPlace)

  const { closeModal, goBack } = useModalCallbacks()

  const reviewsLabel = intl.formatMessage({
    id: 'qLROjUVMWU',
    defaultMessage: 'Reviews',
    description: 'Label for the Google reviews section in thing to do trip item summary',
  })
  const startsEndsSubtitle = intl.formatMessage(
    {
      id: 'MPmpl0/Apf',
      defaultMessage: 'Starts in {startPlace}, Ends in {endPlace}',
      description: 'Subtitle showing start and end locations for a thing to do trip item',
    },
    {
      startPlace: item.startPlace.name,
      endPlace: item.endPlace.name,
    },
  )

  const subtitle =
    isSameEndPlace || !item.endPlace.name ? item.startPlace.name : startsEndsSubtitle

  return (
    <>
      <ModalHeader
        title={getDisplayableCustomThingToDoName(item)}
        subtitle={subtitle}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />

      <ModalBody>
        <SummaryImage
          place={item.startPlace}
          tripId={item.tripId}
          tripItemId={item.id}
        />
        <ModalContent>
          <VerticalSpacer gap={20}>
            <Group direction="horizontal" horizontalAlign="space-between">
              <Heading variant="heading6">
                {intl.formatMessage(ItemMetadata[item.type].singularName)}
              </Heading>
              {item.isBooked && <BookedLabel />}
            </Group>
            <StartAndEndPlaces item={item} />
            {placeId && (
              <GooglePlaceRating placeId={placeId} size="M" label={reviewsLabel} />
            )}
            <ConfirmationDetails
              isBooked={item.isBooked}
              confirmationCode={item.confirmationCode}
              price={item.price}
            />
            <ExtraDetails
              url={item.url}
              phone={item.phone}
              notes={item.notes}
            />
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>

      <DefaultSummaryModalFooter item={item} />
    </>
  )
}

export default ThingTodoItemSummary
